/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from "@emotion/core";
import { debounce } from "@material-ui/core";
import React, {
  useState,
  useEffect,
  ComponentProps,
  useRef,
  useCallback,
  useMemo,
} from "react";
import type { CommentFilterType, GiftItemData, StreamingType } from "../../modules/streaming";
import icon_up_arrow from "../../designs/images/icon_up_arrow.png";
import { EventInfo } from "../../utility";
import { StreamingCommentItem } from "./StreamingCommentItem";
import { Timestamp } from "@google-cloud/firestore";
import { authState } from "modules/auth";
import { useI18n } from "hooks/i18n/i18n";
import { useFilteringComment } from "hooks/Streaming/useFilteringComment";
import type { THEOPlayer } from "@spwn/types/external";
import type { ProductData, UserMessage } from "@spwn/types/firebase/firestore";

type Props = {
  eventStart: Timestamp;
  streamingType: StreamingType;
  player: THEOPlayer;
  eventInfo: EventInfo;
  giftItemMap: { [key: string]: GiftItemData };
  currentVideoId: string; // !!! don't allow null !!!
  displayComments: UserMessage[];
  eventGoodsMap: { [goodsId: string]: ProductData };
  user: authState["user"];
  isDarkModeEnabled: boolean;
  THEOPlayerElementHeight?: number;
  commentFilterState: CommentFilterType;
  banUser: ComponentProps<typeof StreamingCommentItem>["onClickBanUser"];
  banComment: ComponentProps<typeof StreamingCommentItem>["onClickBanComment"];
  deleteComment: ComponentProps<
    typeof StreamingCommentItem
  >["msgDeleteFunction"];
};

const StreamingComment: React.FC<Props> = (props) => {
  const [autoScroll, setAutoScroll] = useState<boolean>(true);
  const classesStreamingCommentDesign = useStreamingCommentDesign({
    streamingType: props.streamingType,
    THEOPlayerElementHeight: props.THEOPlayerElementHeight,
  });
  const classesHideCommentsStyles = useHideCommentsStyles();
  const { t } = useI18n();
  // NGワードをフィルタリングしたものを変数に格納する
  const filteringComments = useFilteringComment(props.displayComments);

  const msgListInnerRef = useRef<HTMLDivElement>(null);

  /**
   * 取得したコメント欄のエレメントを基に、コメント欄の一番下までの高さを取得する
   */
  const getMsgListValue = useCallback(() => {
    if (!msgListInnerRef.current) {
      return null;
    }
    const msgAreaHeight = msgListInnerRef.current.scrollHeight;
    const msgDisplayFlame = msgListInnerRef.current.clientHeight;
    const bottomPosition = msgAreaHeight - msgDisplayFlame;
    return bottomPosition;
  }, []);

  useEffect(() => {
    // HOTFIX: テストの場合は onscroll を無視したい
    if (!msgListInnerRef.current || process.env.NODE_ENV === "test") {
      return;
    }

    const listElement = msgListInnerRef.current;

    // ↓ボタンがチラつくのを防止するためdebounceしている
    const onScroll = debounce(() => {
      const bottomPosition = getMsgListValue();
      if (bottomPosition === null) {
        return;
      }
      const scrollValue = listElement.scrollTop;
      setAutoScroll(bottomPosition - scrollValue <= 1);
    }, 100);
    listElement.addEventListener("scroll", onScroll);
    return () => listElement.removeEventListener("scroll", onScroll);
  }, [getMsgListValue]);

  useEffect(() => {
    // scroll to bottom when auto scroll mode and displayComments is updated
    const bottomPosition = getMsgListValue();
    if (autoScroll && bottomPosition !== null) {
      msgListInnerRef.current?.scroll({
        top: bottomPosition,
        behavior: "smooth",
      });
    }
  }, [autoScroll, getMsgListValue, props.displayComments]);

  /**
   * ギフトアイテムの金額リストを作成する
   */
  const giftAmountList = useMemo<number[]>(() => {
    const giftAmountList: number[] = [];
    if (props.giftItemMap) {
      Object.keys(props.giftItemMap).forEach((key) => {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const value = props.giftItemMap[key]!.values.paid;
        giftAmountList.push(value);
      });
      giftAmountList.sort((a, b) => a - b);
    }
    return giftAmountList;
  }, [props.giftItemMap]);

  if (props.commentFilterState === "none") {
    return (
      <div css={classesStreamingCommentDesign.root}>
        <div ref={msgListInnerRef} css={classesStreamingCommentDesign.inner}>
          <div>
            <div css={classesStreamingCommentDesign.list}>
              <div css={classesHideCommentsStyles.root}>
                <p>{t("streaming.commentDisplay.hideview")}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div css={classesStreamingCommentDesign.root}>
      <div ref={msgListInnerRef} css={classesStreamingCommentDesign.inner}>
        <div>
          <div css={classesStreamingCommentDesign.list}>
            {filteringComments.map((el) => (
              <StreamingCommentItem
                key={el._id}
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                eventId={props.eventInfo._id!}
                message={el}
                isCommentUser={el.uid === props.user.uid}
                isDarkModeEnabled={props.isDarkModeEnabled}
                giftItemMap={props.giftItemMap}
                eventGoodsMap={props.eventGoodsMap}
                giftAmountList={giftAmountList}
                onClickBanUser={props.banUser}
                onClickBanComment={props.banComment}
                msgDeleteFunction={props.deleteComment}
              />
            ))}
          </div>
        </div>
        <div id="msgEnd"></div>
      </div>
      <div
        id="back_to_bottom"
        css={classesStreamingCommentDesign.button}
        className={autoScroll ? "hide" : undefined}
        onClick={() => setAutoScroll(true)}
      >
        <img src={icon_up_arrow} alt="" />
      </div>
    </div>
  );
};

interface StreamingCommentDesignProps {
  streamingType: string;
  THEOPlayerElementHeight?: number;
}
const useStreamingCommentDesign = (props: StreamingCommentDesignProps) => {
  const actionAreaHeight = useMemo(() => {
    return props.THEOPlayerElementHeight
      ? css`
          height: calc(
            ${props.THEOPlayerElementHeight}px -
            ${props.streamingType === "Live" ? 148 : 46}px
          );
        `
      : null;
  }, [props.THEOPlayerElementHeight, props.streamingType]);

  const root = useMemo(
    () => css`
      position: relative;
      @media screen and (min-width: 768px) {
        display: block;
        padding-top: 38px;
        border: 1px solid #e0e0e0;
      }
    `,
    []
  );
  const inner = useMemo(
    () => css`
      position: relative;
      height: calc(280px);
      overflow-y: scroll;
      overscroll-behavior: none;
      @media screen and (min-width: 768px) {
        width: 100%;
        ${actionAreaHeight}
        min-height: 415px;
      }
    `,
    [actionAreaHeight]
  );
  const list = useMemo(
    () => css`
      padding: 10px 5%;
      @media screen and (min-width: 768px) {
        padding: 10px;
      }
    `,
    []
  );
  const button = useMemo(
    () => css`
      position: absolute;
      bottom: 10px;
      right: 50%;
      width: 30px;
      height: 30px;
      padding: 7px;
      margin-right: -15px;
      background-color: #00c2ae;
      border-radius: 50%;
      visibility: visible;
      opacity: 1;
      transition: 0.3s;
      cursor: pointer;
      box-shadow: 0 2px 2px 0 rgba(0, 0, 0, 0.14),
        0 1px 5px 0 rgba(0, 0, 0, 0.12), 0 3px 1px -2px rgba(0, 0, 0, 0.2);
      &.hide {
        visibility: hidden;
        opacity: 0;
      }
      img {
        width: 100%;
      }
    `,
    []
  );

  return {
    root,
    inner,
    list,
    button,
  };
};

const useHideCommentsStyles = () => {
  return useMemo(
    () => ({
      root: css`
        padding: 10px 10px;
        margin-top: 40px;
        border-radius: 3px;
        background-color: #3e3e3e;
        p {
          color: #fff;
          font-size: 14px;
          line-height: 1.5em;
          letter-spacing: 1px;
        }
      `,
    }),
    []
  );
};

export default StreamingComment;
